import { useCallback, useEffect, useState } from "react";
import { guardarVuelta, pedirCliente, tomarVuelta, type Donde } from "./ir-al-cliente";
import "./paneles.css";
import { HoyPanel } from "./Hoy";

/**
 * «Mando»: todo el trabajo en una pantalla (Jorge, 20/10/2026).
 *
 * Tres partes: Hoy (lo hecho, lo que hay que decidir y lo atascado), Clientes y
 * Personas y máquinas. Todo sale de `/api/mando`, que junta lo que ya se guardaba.
 *
 * Aquí no se aprueba: se lleva a «Tareas pendientes», que es la única puerta para
 * decidir. Dos puertas acaban decidiendo distinto.
 *
 * Rehecho el 22/10/2026 con el lenguaje de `paneles.css`, el mismo que Tareas
 * pendientes: las dos son el panel de trabajo y no pueden parecer dos programas.
 */

interface Punto {
  ficha: string;
  url: string;
  clientId: string | null;
  lista: string;
  que: string;
  responsable: string;
  gravedad: string | null;
}

interface DatosDeMando {
  generado: string;
  hoy: {
    hecho: { titulo: string; cliente: string | null; cuando: string | null }[];
    porDecidir: { id: string; titulo: string; cliente: string | null; caduca: string | null; pedida: string }[];
    faltaEjecutar: { id: string; titulo: string; cliente: string | null; herramienta: string | null }[];
    fallos: { id: string; titulo: string; error: string; cuando: string | null; maquina: string | null; veces?: number; atasca?: boolean }[];
    /** Lo cerrado «hecho sin comprobar» que al día siguiente no estaba hecho (27/10/2026). */
    noQuedo?: { id: string; conversacion: string; titulo: string; cuando: string; que: string }[];
    esperando: { id: string; titulo: string; desde: string; motivo: string | null }[];
  };
  clientes: {
    id: string;
    nombre: string;
    problemasAbiertos: number;
    hechoEsteMes: number;
    ultimoHecho: string[];
    plan: Record<string, number>;
    puntosAbiertos: number | null;
    porDecidir: number;
  }[];
  personas: { quien: string; cuantos: number; graves: number; puntos: Punto[] }[] | null;
  tableroNoContesta: string | null;
  /** Lo que falla en los ordenadores, de la revisión diaria. */
  avisosDeMaquinas?: string[];
  maquinas: {
    id: string;
    nombre: string;
    conectada: boolean;
    ultimaVez: string | null;
    hechos: number;
    fallados: number;
  }[];
}

type Parte = "hoy" | "clientes" | "personas";

const HORA = new Intl.DateTimeFormat("es-ES", {
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "Europe/Madrid",
});
const cuando = (f: string | null | undefined) => (f ? HORA.format(new Date(f)) : "");

const hace = (f: string) => {
  const h = Math.round((Date.now() - new Date(f).getTime()) / 3_600_000);
  return h < 48 ? `${h} h` : `${Math.round(h / 24)} días`;
};

/** Una tarea en una línea: el texto entero está en su ficha. */
const corto = (t: string) => (t.length > 140 ? `${t.slice(0, 139).trimEnd()}…` : t);

/** La prisa de un punto del tablero, como color de estado. */
const PUNTO: Record<string, string> = { critico: "mal", urgente: "mal", mirar: "aviso", tranquilo: "ok" };

/** Quién lo hace, dicho como se dice en casa. El valor es la etiqueta del tablero. */
const QUIEN = [
  ["Bilbolink", "Bilbolink (tú)"],
  ["Jorge Luis", "Jorge Luis"],
  ["Director", "BilboBot"],
] as const;

/**
 * Añadir una tarea a un cliente. Va a su ficha de Trello, la misma lista que lee
 * Mando, así que aparece en los dos sitios a la vez.
 */
function NuevaTarea({ clienteId, alTerminar }: { clienteId: string; alTerminar: (texto: string) => void }) {
  const [texto, setTexto] = useState("");
  const [quien, setQuien] = useState<string>("Bilbolink");
  const [prisa, setPrisa] = useState<string>("mirar");
  const [fecha, setFecha] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [fallo, setFallo] = useState<string | null>(null);

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    setEnviando(true);
    setFallo(null);
    try {
      const r = await fetch(`/api/mando/clientes/${clienteId}/tarea`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ texto, responsable: quien, gravedad: prisa, ...(fecha ? { fecha } : {}) }),
      });
      const cuerpo = (await r.json()) as { error?: string; ficha?: string };
      if (!r.ok) throw new Error(cuerpo.error ?? `Error ${r.status}`);
      setTexto("");
      setFecha("");
      alTerminar(`Tarea añadida a la ficha «${cuerpo.ficha}».`);
    } catch (error) {
      setFallo(error instanceof Error ? error.message : String(error));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form className="mn-nueva" onSubmit={(e) => void guardar(e)}>
      <label>
        Nueva tarea
        <input
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Qué hay que hacer"
          maxLength={400}
          required
        />
      </label>
      <label>
        Quién
        <select value={quien} onChange={(e) => setQuien(e.target.value)}>
          {QUIEN.map(([v, n]) => (
            <option key={v} value={v}>
              {n}
            </option>
          ))}
        </select>
      </label>
      <label>
        Prisa
        <select value={prisa} onChange={(e) => setPrisa(e.target.value)}>
          <option value="urgente">Urgente</option>
          <option value="mirar">Mirar</option>
          <option value="tranquilo">Tranquilo</option>
        </select>
      </label>
      <label>
        Para (opcional)
        <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
      </label>
      <button className="pnl-boton si" type="submit" disabled={enviando || texto.trim().length < 4}>
        {enviando ? "Añadiendo…" : "Añadir"}
      </button>
      {fallo && <div className="mn-fallo">{fallo}</div>}
    </form>
  );
}

/**
 * Lo sin cobrar en Holded (Jorge, 27/10/2026: «¿tengo que usar Holded para ver
 * quién ha pagado o no?»). Se lee de Holded: si alguien ya pagó, se marca allí
 * y aquí desaparece al volver a mirar.
 */
interface ClienteSinCobrar {
  cliente: string;
  total: number;
  vencidas: number;
  peorRetraso: number;
  facturas: { numero: string; total: number; fecha: string; vencimiento: string | null; diasDeRetraso: number }[];
}

const EUROS = new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" });

function SinCobrar() {
  const [datos, setDatos] = useState<{ disponible: boolean; error?: string; clientes: ClienteSinCobrar[]; mirado: string | null } | null>(null);
  const [mirando, setMirando] = useState(false);
  const [abierto, setAbierto] = useState<string | null>(null);

  const mirar = useCallback(async (otraVez = false) => {
    setMirando(true);
    try {
      const r = await fetch(`/api/mando/cobros${otraVez ? "?otraVez=1" : ""}`, { credentials: "same-origin" });
      if (r.ok) setDatos(await r.json());
    } finally {
      setMirando(false);
    }
  }, []);
  useEffect(() => void mirar(), [mirar]);

  if (!datos) return null;
  if (!datos.disponible) return null;
  const total = datos.clientes.reduce((n, c) => n + c.total, 0);
  const facturas = datos.clientes.reduce((n, c) => n + c.facturas.length, 0);
  const vencidas = datos.clientes.reduce((n, c) => n + c.vencidas, 0);

  return (
    <section className="pnl-seccion">
      <header>
        <h2>Sin cobrar</h2>
        <span className={`pnl-cuenta es-${vencidas ? "aviso" : "ok"}`}>{EUROS.format(total)}</span>
        <button className="pnl-boton ir" disabled={mirando} onClick={() => void mirar(true)}>
          {mirando ? "Mirando Holded…" : "Volver a mirar"}
        </button>
      </header>
      <p className="pnl-sub">
        {facturas} {facturas === 1 ? "factura pendiente" : "facturas pendientes"} en Holded, {vencidas} ya vencidas
        {datos.mirado ? ` · mirado a las ${new Date(datos.mirado).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}` : ""}.
        Si alguien ya pagó, márcala como cobrada en Holded y pulsa «Volver a mirar».
      </p>
      {datos.error && <p className="pnl-vacio">Holded no contestó: {datos.error}</p>}
      {datos.clientes.map((c) => {
        const esteAbierto = abierto === c.cliente;
        return (
          <div key={c.cliente}>
            <button className="pnl-fila" aria-expanded={esteAbierto} onClick={() => setAbierto(esteAbierto ? null : c.cliente)}>
              <span className={`pnl-punto es-${c.peorRetraso > 30 && c.vencidas ? "mal" : c.vencidas ? "aviso" : "ok"}`} />
              <div className="txt">
                <div className="t">{c.cliente}</div>
                <div className="s">
                  {c.facturas.length} {c.facturas.length === 1 ? "factura" : "facturas"}
                  {c.vencidas ? ` · la más vieja vencida hace ${c.peorRetraso} días` : " · aún no vencida"}
                </div>
              </div>
              <span className="pnl-cuenta">{EUROS.format(c.total)}</span>
            </button>
            {esteAbierto && (
              <div className="mn-detalle">
                <ul className="mn-ancho">
                  {c.facturas.map((f) => (
                    <li key={f.numero}>
                      <span>
                        {f.numero} · {EUROS.format(f.total)} · emitida el {f.fecha}
                        {f.vencimiento
                          ? f.diasDeRetraso > 0
                            ? ` · vencida hace ${f.diasDeRetraso} días`
                            : ` · vence el ${f.vencimiento}`
                          : " · sin vencimiento"}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
}

export function Mando({ irA }: { irA: (vista: "tareas" | "memoria") => void }) {
  const [datos, setDatos] = useState<DatosDeMando | null>(null);
  const [error, setError] = useState<string | null>(null);
  /* Si se vuelve de Memoria, a la misma parte y con el mismo cliente abierto. */
  const [vuelta] = useState(() => tomarVuelta());
  const [parte, setParte] = useState<Parte>((vuelta?.parte as Parte | undefined) ?? "hoy");
  const [abierto, setAbierto] = useState<string | null>(vuelta?.abierto ?? null);
  const [reintentando, setReintentando] = useState<string | null>(null);
  const [aviso, setAviso] = useState<{ texto: string; mal?: boolean } | null>(null);

  /*
   * Lo terminado hoy, de la MISMA fuente que «Terminado» de abajo (22/10/2026).
   *
   * La cifra salía de `objectives` y la lista de los trabajos: arriba decía 0 y
   * abajo 3. Dos números para lo mismo en la misma pantalla.
   */
  const [terminadoHoy, setTerminadoHoy] = useState<number | null>(null);

  const cargar = useCallback(async () => {
    try {
      const d = new Date();
      const dia = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      void fetch(`/api/hoy/hecho?dia=${dia}`, { credentials: "same-origin" })
        .then((x) => (x.ok ? x.json() : null))
        .then((x) => x && setTerminadoHoy(((x as { hecho: unknown[] }).hecho ?? []).length))
        .catch(() => undefined);
      const r = await fetch("/api/mando", { credentials: "same-origin" });
      const cuerpo = (await r.json()) as DatosDeMando & { error?: string };
      if (!r.ok) throw new Error(cuerpo.error ?? `Error ${r.status}`);
      setDatos(cuerpo);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  /* Al volver de Memoria, se baja hasta el cliente que estaba abierto. */
  const [bajado, setBajado] = useState(false);
  useEffect(() => {
    if (bajado || !datos || !vuelta?.abierto) return;
    setBajado(true);
    setTimeout(() => document.getElementById(`mando-${vuelta.abierto}`)?.scrollIntoView({ block: "start" }), 50);
  }, [datos, bajado, vuelta]);

  useEffect(() => {
    void cargar();
    const t = setInterval(() => void cargar(), 60_000);
    return () => clearInterval(t);
  }, [cargar]);

  async function reintentar(id: string) {
    setReintentando(id);
    try {
      const r = await fetch(`/api/mando/reintentar/${id}`, { method: "POST", credentials: "same-origin" });
      const cuerpo = (await r.json()) as { error?: string };
      if (!r.ok) throw new Error(cuerpo.error ?? `Error ${r.status}`);
      setAviso({ texto: "Vuelto a la cola. Saldrá en cuanto haya una máquina libre." });
      await cargar();
    } catch (e) {
      setAviso({ texto: `No se pudo reintentar: ${e instanceof Error ? e.message : String(e)}`, mal: true });
    } finally {
      setReintentando(null);
    }
  }

  if (error && !datos)
    return (
      <div className="pnl">
        <div className="pnl-nota es-mal" role="alert">
          <p>No se pudo cargar: {error}</p>
        </div>
      </div>
    );
  if (!datos)
    return (
      <div className="pnl">
        <p className="pnl-sub">Cargando…</p>
      </div>
    );

  const { hoy } = datos;
  const noQuedo = hoy.noQuedo ?? [];
  const atascado = hoy.fallos.length + hoy.esperando.length + hoy.faltaEjecutar.length + noQuedo.length;
  const conectadas = datos.maquinas.filter((m) => m.conectada).length;
  const conPrisa = (datos.personas ?? []).reduce((a, p) => a + p.graves, 0);

  return (
    <div className="pnl">
      <header className="pnl-cabecera">
        <div>
          <h1>Mando</h1>
          <p className="pnl-sub">Todo el trabajo en una pantalla: qué hay, quién lo hace y si está hecho.</p>
        </div>
        <div className="pnl-vistas" role="group" aria-label="Partes de Mando">
          {(
            [
              ["hoy", "Hoy", hoy.porDecidir.length + atascado],
              ["clientes", "Clientes", datos.clientes.length],
              ["personas", "Personas y máquinas", null],
            ] as const
          ).map(([id, nombre, n]) => (
            <button key={id} aria-pressed={parte === id} onClick={() => setParte(id)}>
              {nombre}
              {n ? <span className="n">{n}</span> : null}
            </button>
          ))}
        </div>
      </header>

      <div className="pnl-cifras">
        <button
          className={`pnl-cifra es-${hoy.porDecidir.length ? "aviso" : "ok"}`}
          onClick={() => (hoy.porDecidir.length ? irA("tareas") : setParte("hoy"))}
          title={hoy.porDecidir.length ? "Ir a Tareas pendientes" : undefined}
        >
          <span className="n">{hoy.porDecidir.length}</span>
          <span className="l">por decidir</span>
        </button>
        <button className={`pnl-cifra es-${atascado ? "mal" : "ok"}`} onClick={() => setParte("hoy")}>
          <span className="n">{atascado}</span>
          <span className="l">atascado o fallado</span>
        </button>
        <button
          className="pnl-cifra es-ok"
          onClick={() => {
            setParte("hoy");
            setTimeout(() => document.getElementById("hy-terminado")?.scrollIntoView({ block: "start" }), 50);
          }}
        >
          <span className="n">{terminadoHoy ?? "…"}</span>
          <span className="l">terminado hoy</span>
        </button>
        <button
          className={`pnl-cifra es-${conectadas === 0 || datos.avisosDeMaquinas?.length ? "mal" : "ok"}`}
          onClick={() => setParte("personas")}
        >
          <span className="n">
            {conectadas}
            <span className="u">de {datos.maquinas.length}</span>
          </span>
          <span className="l">máquinas conectadas</span>
        </button>
      </div>

      {aviso && (
        <div className={aviso.mal ? "pnl-nota es-mal" : "pnl-nota"} role="status">
          <p>{aviso.texto}</p>
          <button aria-label="Cerrar" onClick={() => setAviso(null)}>
            ×
          </button>
        </div>
      )}

      {parte === "hoy" && (
        <>
          <section className="pnl-seccion">
            <header>
              <h2>Por decidir</h2>
              <span className={`pnl-cuenta es-${hoy.porDecidir.length ? "aviso" : "ok"}`}>{hoy.porDecidir.length}</span>
              {hoy.porDecidir.length > 0 && (
                <button className="pnl-boton si ir" onClick={() => irA("tareas")}>
                  Decidir en Tareas pendientes
                </button>
              )}
            </header>
            {hoy.porDecidir.length === 0 ? (
              <p className="pnl-vacio">Nada espera tu decisión.</p>
            ) : (
              /*
               * Las actualizaciones de plugins, en UNA línea (26/10/2026): eran
               * treinta y nueve filas iguales. Se aprueban todas de una vez en
               * Tareas pendientes con «Aprobar las N actualizaciones».
               */
              (() => {
                const esDePlugins = (t: string) => /^actualizar \d+ plugins?$/i.test(t.trim());
                const plugins = hoy.porDecidir.filter((a) => esDePlugins(a.titulo));
                const resto = hoy.porDecidir.filter((a) => !esDePlugins(a.titulo));
                const total = plugins.reduce((n, a) => n + Number(/\d+/.exec(a.titulo)?.[0] ?? 0), 0);
                const caduca = plugins.map((a) => a.caduca).filter(Boolean).sort()[0] ?? null;
                return (
                  <>
                    {plugins.length > 0 && (
                      <button className="pnl-fila" onClick={() => irA("tareas")}>
                        <span className="pnl-punto es-aviso" />
                        <div className="txt">
                          <div className="t">
                            Actualizar plugins en {plugins.length} {plugins.length === 1 ? "web" : "webs"} ({total} plugins)
                          </div>
                          <div className="s">
                            Se aprueban de una vez en Tareas pendientes{caduca ? ` · caducan el ${cuando(caduca)}` : ""}
                          </div>
                        </div>
                      </button>
                    )}
                    {resto.map((a) => (
                <div className="pnl-fila" key={a.id}>
                  <span className="pnl-punto es-aviso" />
                  <div className="txt">
                    <div className="t">{a.titulo}</div>
                    <div className="s">
                      {a.cliente ?? "Para toda la casa"} · pedida el {cuando(a.pedida)}
                      {a.caduca ? ` · caduca el ${cuando(a.caduca)}` : ""}
                    </div>
                  </div>
                </div>
                    ))}
                  </>
                );
              })()
            )}
          </section>

          {/*
           * «Te toca» (Jorge, 26/10/2026): lo tuyo de personas, cinco como mucho y lo
           * más urgente primero, para no tener que entrar en Trello a buscarlo. Son
           * los puntos de las fichas con [Bilbolink] (o [Jorge]); cada uno abre su ficha.
           */}
          {(() => {
            const mios = datos.personas?.find((p) => p.quien === "Bilbolink");
            if (!mios) return null;
            return (
              <section className="pnl-seccion">
                <header>
                  <h2>Te toca</h2>
                  <span className={`pnl-cuenta es-${mios.graves ? "mal" : mios.cuantos ? "aviso" : "ok"}`}>{mios.cuantos}</span>
                </header>
                {mios.cuantos === 0 ? (
                  <p className="pnl-vacio">No tienes nada apuntado en las fichas.</p>
                ) : (
                  <>
                    {mios.puntos.slice(0, 5).map((x, i) => (
                      <a className="pnl-fila" key={i} href={x.url} target="_blank" rel="noreferrer">
                        <span className={`pnl-punto es-${PUNTO[x.gravedad ?? ""] ?? ""}`} />
                        <div className="txt">
                          <div className="t">{corto(x.que)}</div>
                          <div className="s">
                            {x.ficha} · {x.lista}
                          </div>
                        </div>
                      </a>
                    ))}
                    {mios.cuantos > 5 && (
                      <button className="pnl-fila" onClick={() => { setParte("personas"); setAbierto("p:Bilbolink"); }}>
                        <div className="txt">
                          <div className="s">…y {mios.cuantos - 5} más, en Personas</div>
                        </div>
                      </button>
                    )}
                  </>
                )}
              </section>
            );
          })()}

          <SinCobrar />

          <section className="pnl-seccion">
            <header>
              <h2>Atascado</h2>
              <span className={`pnl-cuenta es-${atascado ? "mal" : "ok"}`}>{atascado}</span>
            </header>
            {atascado === 0 && <p className="pnl-vacio">Nada atascado ni fallado en los últimos 7 días.</p>}
            {hoy.fallos.map((f) => (
              <div className="pnl-fila" key={f.id}>
                <span className="pnl-punto es-mal" />
                <div className="txt">
                  <div className="t">{f.titulo}</div>
                  <div className="s">
                    {f.veces && f.veces > 1 ? `Falló ${f.veces} veces por lo mismo, la última el ` : "Falló el "}
                    {cuando(f.cuando)}
                    {f.maquina ? ` en ${f.maquina}` : ""}: {f.error || "sin motivo apuntado"}
                    {f.atasca ? " No se vuelve a intentar sola hasta que se arregle o pulses Reintentar." : ""}
                  </div>
                </div>
                <button className="pnl-boton" disabled={reintentando === f.id} onClick={() => void reintentar(f.id)}>
                  {reintentando === f.id ? "Reintentando…" : "Reintentar"}
                </button>
              </div>
            ))}
            {hoy.esperando.map((j) => (
              <div className="pnl-fila" key={j.id}>
                <span className="pnl-punto es-aviso" />
                <div className="txt">
                  <div className="t">{j.titulo}</div>
                  <div className="s">
                    Lleva {hace(j.desde)} esperando máquina{j.motivo ? `: ${j.motivo}` : ""}
                  </div>
                </div>
              </div>
            ))}
            {noQuedo.map((n) => (
              <div className="pnl-fila" key={n.id}>
                <span className="pnl-punto es-mal" />
                <div className="txt">
                  <div className="t">{n.titulo}</div>
                  <div className="s">
                    El director lo dio por hecho sin comprobarlo y al día siguiente ({cuando(n.cuando)}) no estaba: {n.que}
                  </div>
                </div>
                <a className="pnl-boton" href={`/?conversacion=${encodeURIComponent(n.conversacion)}`}>
                  Ver el chat
                </a>
              </div>
            ))}
            {hoy.faltaEjecutar.map((a) => (
              <div className="pnl-fila" key={a.id}>
                <span className="pnl-punto es-aviso" />
                <div className="txt">
                  <div className="t">{a.titulo}</div>
                  <div className="s">
                    Aprobada y sin hacer: {a.cliente ?? "sin cliente"}. Pídeselo al director en el chat
                    {a.herramienta ? ` (${a.herramienta})` : ""}.
                  </div>
                </div>
              </div>
            ))}
          </section>

          {/*
            Lo que era la pestaña Hoy (22/10/2026): lo que corre ahora, lo
            terminado con su tabla de antes y ahora, los avisos, lo que hay por
            hacer y el sistema. «Hecho en 24 h» se quita: «Terminado» es lo mismo
            con el día a elegir.
          */}
          <HoyPanel irA={() => irA("tareas")} dentroDelMando />
        </>
      )}

      {parte === "clientes" && (
        <section className="pnl-seccion">
          <header>
            <h2>Clientes</h2>
            <span className="pnl-cuenta">{datos.clientes.length}</span>
          </header>
          {datos.clientes.map((c) => {
            const plan = (c.plan.propuesta ?? 0) + (c.plan.hecha ?? 0) + (c.plan.descartada ?? 0);
            const suyos = datos.personas?.flatMap((p) => p.puntos).filter((p) => p.clientId === c.id) ?? [];
            const abiertoEste = abierto === c.id;
            const dato = (n: number, texto: string) => (
              <span className={n ? "mn-dato" : "mn-dato cero"}>
                {n} {texto}
              </span>
            );
            return (
              <div key={c.id} id={`mando-${c.id}`}>
                <button className="pnl-fila" aria-expanded={abiertoEste} onClick={() => setAbierto(abiertoEste ? null : c.id)}>
                  <span className="chev" aria-hidden="true">
                    ›
                  </span>
                  <div className="txt">
                    <div className="t">{c.nombre}</div>
                    <div className="mn-cliente-cifras">
                      {dato(c.hechoEsteMes, "hecho este mes")}
                      {dato(c.problemasAbiertos, "problemas de auditoría")}
                      {c.puntosAbiertos !== null && dato(c.puntosAbiertos, "tareas en su ficha")}
                      {plan > 0 && (
                        <span className="mn-barra" title={`${c.plan.hecha ?? 0} de ${plan} acciones del plan hechas`}>
                          plan
                          <i>
                            <b style={{ width: `${Math.round(((c.plan.hecha ?? 0) / plan) * 100)}%` }} />
                          </i>
                          {c.plan.hecha ?? 0}/{plan}
                        </span>
                      )}
                    </div>
                  </div>
                  {c.porDecidir > 0 && <span className="pnl-cuenta es-aviso">{c.porDecidir} por decidir</span>}
                </button>
                {abiertoEste && (
                  <div className="mn-detalle">
                    <div className="mn-enlaces mn-ancho">
                      {(
                        [
                          ["estrategia", "Su estrategia"],
                          ["informes", "Sus informes mensuales"],
                          ["contenidos", "Sus contenidos"],
                        ] as [Donde, string][]
                      ).map(([donde, nombre]) => (
                        <button
                          key={donde}
                          className="pnl-boton"
                          onClick={() => {
                            pedirCliente(c.id, donde);
                            guardarVuelta({ parte: "clientes", abierto: c.id });
                            irA("memoria");
                          }}
                        >
                          {nombre}
                        </button>
                      ))}
                      {suyos[0] && (
                        <a className="pnl-boton" href={suyos[0].url} target="_blank" rel="noreferrer">
                          Su ficha en Trello ↗
                        </a>
                      )}
                    </div>
                    <div>
                      <h3>Lo último hecho este mes</h3>
                      {c.ultimoHecho.length === 0 ? (
                        <p className="nada">Nada apuntado este mes.</p>
                      ) : (
                        <ul>
                          {c.ultimoHecho.map((t, i) => (
                            <li key={i}>
                              <span className="pnl-punto es-ok" />
                              <span>{t}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div>
                      <h3>Tareas abiertas en su ficha</h3>
                      {suyos.length === 0 ? (
                        <p className="nada">Ninguna.</p>
                      ) : (
                        <ul>
                          {suyos.slice(0, 15).map((p, i) => (
                            <li key={i}>
                              <span className={`pnl-punto es-${PUNTO[p.gravedad ?? ""] ?? ""}`} />
                              <span>
                                {corto(p.que)} <span className="quien">· {p.responsable}</span>
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className="mn-ancho">
                      <NuevaTarea
                        clienteId={c.id}
                        alTerminar={(t) => {
                          setAviso({ texto: t });
                          void cargar();
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </section>
      )}

      {parte === "personas" && (
        <>
          <section className="pnl-seccion">
            <header>
              <h2>Personas</h2>
              {conPrisa > 0 && <span className="pnl-cuenta es-mal">{conPrisa} con prisa</span>}
            </header>
            {datos.tableroNoContesta && (
              <p className="pnl-vacio">No se pudo leer el tablero: {datos.tableroNoContesta}</p>
            )}
            {(datos.personas ?? []).map((p) => {
              const abiertoEste = abierto === `p:${p.quien}`;
              return (
                <div key={p.quien}>
                  <button
                    className="pnl-fila"
                    aria-expanded={abiertoEste}
                    onClick={() => setAbierto(abiertoEste ? null : `p:${p.quien}`)}
                  >
                    <span className="chev" aria-hidden="true">
                      ›
                    </span>
                    <div className="txt">
                      <div className="t">{p.quien}</div>
                      <div className="s">
                        {p.cuantos} {p.cuantos === 1 ? "tarea abierta" : "tareas abiertas"}
                      </div>
                    </div>
                    {p.graves > 0 && <span className="pnl-cuenta es-mal">{p.graves} urgentes</span>}
                  </button>
                  {abiertoEste && (
                    <div className="mn-detalle">
                      <ul className="mn-ancho">
                        {p.puntos.slice(0, 60).map((x, i) => (
                          <li key={i}>
                            <span className={`pnl-punto es-${PUNTO[x.gravedad ?? ""] ?? ""}`} />
                            <span>
                              {corto(x.que)}{" "}
                              <a className="quien" href={x.url} target="_blank" rel="noreferrer">
                                · {x.ficha}
                              </a>
                            </span>
                          </li>
                        ))}
                      </ul>
                      {p.puntos.length > 60 && <p className="nada mn-ancho">…y {p.puntos.length - 60} más</p>}
                    </div>
                  )}
                </div>
              );
            })}
          </section>

          <section className="pnl-seccion">
            <header>
              <h2>Máquinas</h2>
              <span className={`pnl-cuenta es-${datos.avisosDeMaquinas?.length ? "mal" : "ok"}`}>
                {datos.avisosDeMaquinas?.length
                  ? `${datos.avisosDeMaquinas.length} ${datos.avisosDeMaquinas.length === 1 ? "aviso" : "avisos"}`
                  : "todo bien"}
              </span>
            </header>
            {(datos.avisosDeMaquinas ?? []).map((a, i) => (
              <div className="pnl-fila" key={`aviso-${i}`}>
                <span className="pnl-punto es-mal" />
                <div className="txt">
                  <div className="t">{a}</div>
                </div>
              </div>
            ))}
            {datos.maquinas.map((m) => (
              <div className="pnl-fila" key={m.id}>
                <span className={`pnl-punto es-${m.conectada ? "ok" : "apagado"}`} />
                <div className="txt">
                  <div className="t">{m.nombre}</div>
                  <div className="s">
                    {m.conectada ? "Conectada" : `Desconectada${m.ultimaVez ? ` desde el ${cuando(m.ultimaVez)}` : ""}`} ·{" "}
                    en 30 días: {m.hechos} trabajos bien, {m.fallados} mal
                  </div>
                </div>
              </div>
            ))}
          </section>
        </>
      )}

      <p className="mn-pie">Actualizado el {cuando(datos.generado)}. Se refresca cada minuto.</p>
    </div>
  );
}
